import { goBack } from "~/shared_states/modal";
import { initParams } from "../../data/auth";
import { config } from "../../data/config";
import { CardDetails } from "~/components/card_details_form";

declare const Razorpay: any;

export type RazorpayMode = "CARD" | "UPI" | "CARD_AND_UPI";

interface RzpResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

type RazorpayPaymentFailedResponse = {
  error: {
    code: string;
    description: string;
    field: string | null;
    source: string;
    step: string;
    reason: string;
    metadata: {
      payment_id: string;
      order_id: string;
    };
  };
};

interface BaseCheckoutProps {
  orderId: string;
  amount: number;
  email: string;
  phone: string;
  key?: string;
  recurring?: string;
  customer_id?: string;
  onSuccess?: (response: any) => void;
  onFailure?: (response: RazorpayPaymentFailedResponse) => void;
}

export type RazorpayCheckoutProps = BaseCheckoutProps & {
  config?: {
    [key: string]: any;
  };
  mode: RazorpayMode;
  onDismiss?: () => void;
};

export type CustomCardCheckoutProps = BaseCheckoutProps & {
  cardDetails: CardDetails;
  notes?: { [key: string]: any };
};

export async function openRazorpayCheckout(props: RazorpayCheckoutProps) {
  const options: { [key: string]: any } = {
    key: props.key ?? config.razorpayKey,
    order_id: props.orderId,
    amount: props.amount * 100,
    currency: "INR",
    ...(props.recurring && {
      recurring: props.recurring,
    }),
    ...(props.customer_id && {
      customer_id: props.customer_id,
    }),
  };

  if (props.mode == "UPI" && initParams().webview) {
    options["webview_intent"] = true;
  }

  const cardConfig = {
    prefill: { email: props.email, contact: props.phone, method: "card" },
    config: {
      display: {
        blocks: {
          banks: {
            name: "Pay via Card",
            instruments: [{ method: "card" }],
          },
        },
        sequence: ["block.banks"],
        preferences: {
          show_default_blocks: false,
        },
      },
    },
  };

  // enable collect only in non prod env so that we can easily
  // test failure cases
  const upiFlows =
    config.env === "Prod" ? ["intent", "qr"] : ["intent", "qr", "collect"];
  const upiFlowsToHide = config.env === "Prod" ? ["collect"] : [];
  const upiConfig = {
    prefill: {
      email: props.email,
      contact: props.phone,
      method: "upi",
      flows: upiFlows,
    },
    config: {
      display: {
        blocks: {
          upi: {
            name: "Pay via UPI",
            instruments: [{ method: "upi", flows: upiFlows }],
          },
        },
        hide: [
          {
            method: "upi",
            flows: upiFlowsToHide,
          },
        ],
        sequence: ["block.upi"],
        preferences: {
          show_default_blocks: false,
        },
      },
    },
  };

  const cardAndUpiConfig = {
    prefill: {
      email: props.email,
      contact: props.phone,
      method: "upi",
      flows: upiFlows,
    },
    config: {
      display: {
        blocks: {
          upi: {
            name: "Pay via UPI",
            instruments: [{ method: "upi", flows: upiFlows }],
          },
          banks: {
            name: "Pay via Card",
            instruments: [{ method: "card" }],
          },
        },
        hide: [
          {
            method: "upi",
            flows: upiFlowsToHide,
          },
        ],
        sequence: ["block.banks", "block.upi"],
        preferences: {
          show_default_blocks: false,
        },
      },
    },
  };

  // Use config if provided, otherwise use mode-based configuration
  const finalConfig =
    props.config ||
    (props.mode == "CARD_AND_UPI"
      ? cardAndUpiConfig
      : props.mode == "UPI"
        ? upiConfig
        : cardConfig);

  const Razorpay = await getRazorpayInstance();
  const razorpayInitParams = {
    ...options,
    ...finalConfig,
    modal: {
      ondismiss: function () {
        props.onDismiss?.();
        console.log("Rzp closed");
        // commenting out additonal pop, looks like rzp fixed on their end
        // setTimeout(() => {
        //   goBack();
        // }, 500);
      },
    },
    handler:
      props.onSuccess ??
      function (response: any) {
        console.log(response);
      },
  };
  // temporary log
  console.log("razorpayInitParams", JSON.stringify(razorpayInitParams));
  const rzp = Razorpay(razorpayInitParams);
  rzp.on(
    "payment.failed",
    props.onFailure ??
      function (response: RazorpayPaymentFailedResponse) {
        console.error(JSON.stringify(response, null, 2));
      }
  );
  rzp.open();
}

async function getRazorpayInstance(): Promise<any> {
  // @ts-ignore
  if (window && window.Razorpay) {
    return Razorpay;
  } else {
    const s = document.createElement("script");
    s.type = "text/javascript";
    s.async = false;
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    const x = document.getElementsByTagName("script")[0];
    x.parentNode?.insertBefore(s, x);

    return await new Promise((resolve, reject) => {
      s.onload = function () {
        // @ts-ignore
        resolve(window.Razorpay);
      };
    });
  }
}

async function getRazorpayCustomInstance(): Promise<any> {
  // @ts-ignore
  if (window && window.Razorpay) {
    return Razorpay;
  } else {
    const x = document.getElementsByTagName("script")[0];

    // Load Razorpay script
    const s = document.createElement("script");
    s.type = "text/javascript";
    s.async = false;
    s.src = "https://checkout.razorpay.com/v1/razorpay.js";
    x.parentNode?.insertBefore(s, x);

    // Load jQuery script
    const jqueryScript = document.createElement("script");
    jqueryScript.type = "text/javascript";
    jqueryScript.async = false;
    jqueryScript.src =
      "https://ajax.googleapis.com/ajax/libs/jquery/3.3.1/jquery.min.js";
    x.parentNode?.insertBefore(jqueryScript, x);

    return await new Promise((resolve, reject) => {
      s.onload = function () {
        // @ts-ignore
        resolve(window.Razorpay);
      };
    });
  }
}

export async function collectPaymentViaRazorpayCustomCheckout(
  props: CustomCardCheckoutProps
) {
  const Razorpay = await getRazorpayCustomInstance();

  const razorpay = new Razorpay({
    key: props.key ?? config.razorpayKey,
    image: "https://i.imgur.com/n5tjHFD.jpg",
  });

  const data = {
    amount: props.amount * 100,
    currency: "INR",
    email: props.email,
    contact: props.phone,
    notes: props.notes || {},
    order_id: props.orderId,
    ...(props.customer_id && { customer_id: props.customer_id }),
    ...(props.recurring && { recurring: props.recurring }),
    save: 0,
    consent_to_save_card: 0,
    method: "card",
    "card[name]": props.cardDetails.name,
    "card[number]": props.cardDetails.number,
    "card[cvv]": props.cardDetails.cvv,
    "card[expiry_month]": props.cardDetails.expiry_month,
    "card[expiry_year]": props.cardDetails.expiry_year,
  };

  console.log("data", JSON.stringify(data, null, 2));

  razorpay.createPayment(data);

  razorpay.on("payment.success", function (response: any) {
    props.onSuccess?.(response);
  });

  razorpay.on(
    "payment.error",
    function (response: RazorpayPaymentFailedResponse) {
      console.log("payment.error", JSON.stringify(response, null, 2));
      props.onFailure?.(response);
    }
  );
}
